// src/hooks/useReglagesBloc.ts — SCE Issues
// Les réglages d'un écran (tableau de /issues, liste /fnct…) : lus au démarrage,
// écrits après chaque geste (debounce), pour qu'ils restent d'une visite à
// l'autre — et d'un poste à l'autre. Chaque écran a SON bloc du JSON `Prefs`.
//
// ─── Deux copies, une seule vérité ───
//
// - SharePoint (le bloc de la ligne de l'utilisateur) fait foi.
// - Un cache localStorage, clé par utilisateur, sert à afficher l'écran TOUT DE
//   SUITE réglé comme la dernière fois, sans attendre le réseau.
//
// Un réglage touché avant l'arrivée de la lecture SharePoint l'emporte sur elle.
//
// ⚠️ Bascule de rôle (Lot 7) : on lit les réglages de la personne affichée, on
//    n'écrit RIEN — ni SharePoint, ni cache — sinon l'administrateur
//    écraserait ceux de Thibaut en regardant son écran.

import { useCallback, useEffect, useRef, useState } from 'react'
import { ecrireBlocPrefs, lireBlocPrefs } from '@/api/userPrefs.api'
import { useCurrentUser } from './useCurrentUser'

const DEBOUNCE_MS = 600

export interface UseReglagesBloc<T> {
  reglages: T
  /** Modifie les réglages et programme leur enregistrement. */
  modifier: (changer: (prev: T) => T) => void
  /** Vrai tant que la lecture SharePoint n'est pas revenue. */
  chargement: boolean
}

/**
 * @param cleBloc  le bloc du JSON `Prefs` (`listeProblemes`, `listeFnct`)
 * @param prefixeCache  la clé localStorage, suivie de l'UPN
 * @param lire  lecture tolérante (un JSON absent ou abîmé donne les défauts)
 * @param cleLecture  quand elle change, la lecture est refaite (ex. interne / fournisseur)
 * @param actif  faux : ni lecture ni écriture, les défauts seulement (accueil
 *               personnalisable en essai : rien n'est lu pour les autres)
 */
export function useReglagesBloc<T>(cleBloc: string, prefixeCache: string, lire: (brut: unknown) => T, cleLecture = '', actif = true): UseReglagesBloc<T> {
  const { user, enVueRole } = useCurrentUser()
  const upn = user?.upn ?? ''
  const cleCache = (qui: string): string => `${prefixeCache}:${qui.trim().toLowerCase()}`

  const lireRef = useRef(lire)
  lireRef.current = lire
  const [reglages, setReglages] = useState<T>(() => lire(undefined))
  const [chargement, setChargement] = useState(true)

  // Lus par la sauvegarde différée, dont le callback est stable.
  const lectureSeule = useRef(enVueRole || !actif)
  lectureSeule.current = enVueRole || !actif
  const courant = useRef(upn)
  courant.current = upn
  const aEteModifie = useRef(false)
  const minuteur = useRef<ReturnType<typeof setTimeout> | null>(null)
  const enAttente = useRef<T | null>(null)

  const ecrireCache = useCallback(
    (qui: string, r: T): void => {
      try {
        window.localStorage.setItem(`${prefixeCache}:${qui.trim().toLowerCase()}`, JSON.stringify(r))
      } catch {
        // Stockage plein ou bloqué : l'écran reste utilisable, SharePoint garde la copie.
      }
    },
    [prefixeCache],
  )

  // ─── Lecture : cache d'abord, SharePoint ensuite ───
  useEffect(() => {
    if (!upn || !actif) {
      setReglages(lireRef.current(undefined))
      setChargement(false)
      return
    }
    let annule = false
    aEteModifie.current = false
    let enCache: T | null = null
    if (!enVueRole) {
      try {
        const brut = window.localStorage.getItem(cleCache(upn))
        enCache = brut ? lireRef.current(JSON.parse(brut) as unknown) : null
      } catch {
        enCache = null
      }
    }
    setReglages(enCache ?? lireRef.current(undefined))
    setChargement(true)

    lireBlocPrefs(upn, cleBloc)
      .then((bloc) => {
        if (annule || aEteModifie.current) return
        if (bloc !== undefined) {
          const lus = lireRef.current(bloc)
          setReglages(lus)
          if (!enVueRole) ecrireCache(upn, lus)
        }
      })
      .catch((err: unknown) => {
        // Hors ligne ou droit refusé : on garde le cache / les défauts, sans bruit.
        // eslint-disable-next-line no-console
        console.warn(`[useReglagesBloc] lecture de ${cleBloc} échouée:`, err)
      })
      .finally(() => {
        if (!annule) setChargement(false)
      })

    return () => {
      annule = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upn, enVueRole, cleBloc, cleLecture, actif])

  // ─── Écriture différée ───
  const programmer = useCallback(
    (suivants: T): void => {
      enAttente.current = suivants
      if (minuteur.current) clearTimeout(minuteur.current)
      minuteur.current = setTimeout(() => {
        minuteur.current = null
        const qui = courant.current
        const a_ecrire = enAttente.current
        if (!qui || !a_ecrire || lectureSeule.current) return
        void ecrireBlocPrefs(qui, cleBloc, a_ecrire).catch((err: unknown) => {
          // eslint-disable-next-line no-console
          console.warn(`[useReglagesBloc] enregistrement de ${cleBloc} échoué:`, err)
        })
      }, DEBOUNCE_MS)
    },
    [cleBloc],
  )

  // Un démontage en plein debounce ne doit pas perdre le dernier geste.
  useEffect(
    () => () => {
      if (!minuteur.current) return
      clearTimeout(minuteur.current)
      const qui = courant.current
      const a_ecrire = enAttente.current
      if (qui && a_ecrire && !lectureSeule.current) {
        void ecrireBlocPrefs(qui, cleBloc, a_ecrire).catch(() => undefined)
      }
    },
    [cleBloc],
  )

  const modifier = useCallback(
    (changer: (prev: T) => T): void => {
      aEteModifie.current = true
      setReglages((prev) => {
        const suivants = changer(prev)
        if (suivants === prev) return prev
        if (!lectureSeule.current) {
          if (courant.current) ecrireCache(courant.current, suivants)
          programmer(suivants)
        }
        return suivants
      })
    },
    [programmer, ecrireCache],
  )

  return { reglages, modifier, chargement }
}
